import React from "react";
import { ShallowEntity } from "../types";
import { useEntityNavigate } from "../entityNavigation";
import { Link, useLocation } from "react-router-dom";
import EntityIcon from "../Components/EntityIcon";
import LinkedEntityPendingReviewBadge from "../Components/LinkedEntityPendingReviewBadge";
import { openInteractionInViewer } from "./openInViewer";
import { prefetchAttrs } from "../../Data/visibilityPrefetch";
import { EntityPreviewContext } from "../../../RmsEntityPreviewContext";
import { StillPageContext } from "../../design/stillPage";
import { shouldNavigateToEntity } from "../../rmsEntityRouting";
import {
  rememberReturnRow,
  TableRowIdContext,
} from "../../Search/TablePanel/hooks/useTableReturnHighlight";

interface InlineEntityCardProps {
  entity: ShallowEntity;
  onEntityClick?: (entity: ShallowEntity) => void;
  /** Optional extra classes applied to the EntityIcon. Polymorphic cells
   *  pass `flex-shrink-0` so the icon stays visible in narrow columns. */
  iconClassName?: string;
  /** Omit the leading EntityIcon. Used when rendering inside composite
   *  scalar+entity values where the icon would sit between the two parts
   *  and push the entity away from its separator. */
  hideIcon?: boolean;
  /** Quiet link dress for feed prose (#11426 Aug-31, CD ruling,
   *  superseding round-6's at-rest underline there): ink-2 with the
   *  underline arriving on hover, so a stack of references reads as
   *  sentences rather than boxes. Also NON-ATOMIC: a truncating ancestor
   *  cannot ellipsize inside an atomic inline box — Blink drops the box
   *  whole, which deleted the object from a one-line feed row. Two traps
   *  make this stricter than "add display:inline": a BUTTON is blockified
   *  (a specified inline computes to inline-block, still atomic), so the
   *  interaction/onClick branches render a role="button" span instead;
   *  and the preflight makes svg display:block, which would split the
   *  inline box onto its own line, so the kind glyph is forced inline.
   *  Everywhere else keeps the at-rest underline and the atomic chip. */
  quiet?: boolean;
}

// The two chip dresses. QUIET must stay a NON-ATOMIC inline (see the
// prop's comment); CHIP is the atomic inline-flex every non-feed surface
// keeps.
const QUIET_DRESS =
  "inline max-w-full align-baseline text-[var(--fp-ink-2)] font-medium no-underline hover:underline decoration-[var(--fp-border-strong)] underline-offset-2 hover:text-[var(--fp-accent)] hover:decoration-[var(--fp-accent)]";
const CHIP_DRESS =
  "inline-flex items-center gap-1 max-w-full min-w-0 align-baseline text-[var(--fp-ink-2)] font-medium underline decoration-[var(--fp-border-strong)] underline-offset-2 hover:text-[var(--fp-accent)] hover:decoration-[var(--fp-accent)]";

// The quiet ACTIONABLE chip: one implementation of the non-atomic
// contract — a role="button" span (a real <button> is blockified) with
// keyboard activation — so it has a single place to break instead of one
// per branch.
const QuietActionChip: React.FC<{
  entity: ShallowEntity;
  hideIcon?: boolean;
  iconClassName?: string;
  badge: React.ReactNode;
  onActivate: (e: { stopPropagation: () => void }) => void;
}> = ({ entity, hideIcon, iconClassName, badge, onActivate }) => (
  <span
    role="button"
    tabIndex={0}
    onClick={onActivate}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onActivate(e);
      }
    }}
    className={`${QUIET_DRESS} cursor-pointer`}
  >
    {!hideIcon && (
      <EntityIcon
        kind={entity.kind}
        className={`inline align-[-0.125em] mr-1 ${iconClassName ?? ""}`}
      />
    )}
    <span>{entity.display_name || entity.name}</span>
    {badge}
  </span>
);

const InlineEntityCard = ({
  entity,
  onEntityClick,
  iconClassName,
  hideIcon,
  quiet: quietProp,
}: InlineEntityCardProps) => {
  const entityNavigate = useEntityNavigate();
  const location = useLocation();
  const insidePreviewPane = React.useContext(EntityPreviewContext);
  // The record preview reads as one quiet surface: its references take the
  // quiet dress, with no underline at rest, wherever they are drawn in it.
  const quiet = quietProp || insidePreviewPane;
  const listRowId = React.useContext(TableRowIdContext);
  const still = React.useContext(StillPageContext);

  // Safety check for entity with missing or invalid data
  if (!entity || !entity.id || !entity.name || !entity.kind) {
    console.warn("Invalid entity passed to InlineEntityCard:", entity);
    return (
      <span className="flex gap-1 text-gray-400 italic">
        <EntityIcon kind="unknown" />
        Invalid Entity
      </span>
    );
  }

  // On a page drawn for looking a reference is the record's name, and opens nothing.
  if (still)
    return (
      <span
        className="inline-flex items-center gap-1 min-w-0"
        data-still-entity=""
      >
        {!hideIcon && (
          <EntityIcon kind={entity.kind} className={iconClassName} />
        )}
        <span className="truncate">{entity.display_name || entity.name}</span>
      </span>
    );

  // Tooltip matrix lives in LinkedEntityPendingReviewBadge so every
  // entity-chip surface (this card, EntityNamesList, etc.) renders the
  // same wording from the same (staging, link_staging) data shape.
  const badge = <LinkedEntityPendingReviewBadge entity={entity} />;
  // Interaction entities open their backing document in the side panel.
  // In quiet mode this is a role="button" span, not a <button>: Blink
  // blockifies buttons (inline computes to inline-block — atomic), which
  // is exactly the box the quiet dress exists to avoid.
  if (entity.kind === "interaction") {
    const openViewer = (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      openInteractionInViewer(entity.id, entity.name);
    };
    if (quiet) {
      return (
        <QuietActionChip
          entity={entity}
          hideIcon={hideIcon}
          iconClassName={iconClassName}
          badge={badge}
          onActivate={openViewer}
        />
      );
    }
    return (
      <button
        type="button"
        onClick={openViewer}
        className={`${CHIP_DRESS} cursor-pointer`}
      >
        {!hideIcon && (
          <EntityIcon kind={entity.kind} className={iconClassName} />
        )}
        <span className="truncate">{entity.display_name || entity.name}</span>
        {badge}
      </button>
    );
  }

  // When onEntityClick is provided, use a button instead of a link —
  // except in quiet mode, where a button's blockified box would break
  // the non-atomic contract (see the interaction branch).
  if (onEntityClick) {
    const activate = (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      onEntityClick(entity);
    };
    if (quiet) {
      return (
        <QuietActionChip
          entity={entity}
          hideIcon={hideIcon}
          iconClassName={iconClassName}
          badge={badge}
          onActivate={activate}
        />
      );
    }
    return (
      <button
        type="button"
        onClick={activate}
        className={`${CHIP_DRESS} cursor-pointer`}
      >
        {!hideIcon && (
          <EntityIcon kind={entity.kind} className={iconClassName} />
        )}
        <span className="truncate">{entity.display_name || entity.name}</span>
        {badge}
      </button>
    );
  }

  const linkProps = entityNavigate.isNotetakerRoute
    ? {}
    : { target: "_blank" as const, rel: "noopener noreferrer" };

  // A person renders as ink with the shared at-rest underline, exactly like
  // every other entity reference — a person is an identity, and a filled pill
  // says taxonomy (#11426, keeper ruling A3 / rule 2). No affiliation
  // tooltip either (Hunter's ruling): most people are external, so hover
  // announcing "Outside contact." restated the default.
  return (
    <Link
      {...prefetchAttrs(entity)}
      to={entityNavigate.prepareLink(entity)}
      {...linkProps}
      className={quiet ? QUIET_DRESS : CHIP_DRESS}
      onClick={(e) => {
        // Plain click opens the split-pane RMS entity preview (same
        // pattern as document chips). Modifier-clicks fall through
        // to the anchor's default behavior so Cmd/Ctrl/middle click
        // still open the entity URL in a new tab.
        if (
          e.button === 0 &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.shiftKey &&
          !e.altKey
        ) {
          e.preventDefault();
          e.stopPropagation();
          // Anywhere in the RMS, navigate in-place rather than stacking a
          // preview of an RMS record on top of the RMS. Was detail pages only
          // (#9741); now the whole product (#9746).
          if (
            shouldNavigateToEntity(location.pathname, { insidePreviewPane })
          ) {
            // Rendered inside a list row (a "Related: Funds" chip and the
            // like), so the list can put the user back on this row when they
            // return. Null anywhere else, where there is no row to return to.
            if (listRowId) {
              rememberReturnRow(location.pathname, location.search, listRowId);
            }
            entityNavigate(entity);
            return;
          }
          window.dispatchEvent(
            new CustomEvent("openEntityPreview", {
              detail: { entityKind: entity.kind, entityId: entity.id },
            })
          );
        }
      }}
    >
      {!hideIcon && (
        <EntityIcon
          kind={entity.kind}
          className={
            quiet
              ? `inline align-[-0.125em] mr-1 ${iconClassName ?? ""}`
              : iconClassName
          }
        />
      )}
      <span className={quiet ? undefined : "truncate"}>
        {entity.display_name || entity.name}
      </span>
      {badge}
    </Link>
  );
};

export default InlineEntityCard;
